import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { getShowProductVariantPriceScale, PriceScaleTableDto } from '@/api-sdk';
import { iconStopwatch } from '@/components/icons';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, rootVariablesStyles } from '@/styles';
import { RowData, RowDataValues } from '@/types';
import { i18nInit, setBaseUrl } from '@/utils';

import './fg-table.js';

@customElement('fg-price-scale-table-summary')
@localized()
export class FgPriceScaleTableSummary extends LitElement {
  @property({ type: String })
  product?: string;

  @property({ type: String })
  country?: string;

  @property({ type: String })
  debtor!: string;

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  locale = 'en_us';

  @state()
  priceScalePromise?: Promise<PriceScaleTableDto>;

  @state()
  priceScale?: PriceScaleTableDto;

  @state()
  reducedPriceScaleData?: RowData = [];

  @state()
  hasGrossColumn = false;

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);

    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    if (!this.product && !this.locale) {
      return;
    }

    const priceScalePromise = getShowProductVariantPriceScale({
      code: this.product || '',
      params: {
        locale: this.locale,
        debtor: this.debtor || undefined,
        country: this.country,
      },
    });

    try {
      this.priceScale = await priceScalePromise;
    } catch (error) {
      console.error(
        'Something went wrong while fetching the Variant Price scale',
        error,
      );
    } finally {
      this.reducePriceScaleTableData();
    }
  }

  checkGrossColumnLength() {
    if (!this.priceScale) {
      return;
    }
    const { scales } = this.priceScale;

    Object.values(scales[0]?.tierPrices).some(tierPrice => {
      if (tierPrice.grossPrice !== null) {
        this.hasGrossColumn = true;
      }
    });
  }

  reducePriceScaleTableData() {
    if (!this.priceScale) {
      return;
    }

    this.checkGrossColumnLength();

    const reducedData: RowData = [],
      { quantityRanges, scales } = this.priceScale;

    quantityRanges.map((range, index) => {
      let grossColumn: { subValue: string | undefined } | null = null;
      if (this.hasGrossColumn) {
        grossColumn = {
          subValue: scales[0]?.tierPrices[index]?.grossPrice?.formatted,
        };
      }

      const newRow: RowDataValues = {
        rowName: html`
          <div class="hsp-container-cell">
            <span>${range.minimumQuantity.toString()}</span>
            ${range.hspAvailable
              ? html`
                  <span class="skewed-label" slot="label">
                    <span class="content hsp-container-cell-content">
                      <span class="hsp-container-cell-icon">
                        ${iconStopwatch}
                      </span>
                      <span>${t('labels.24h')}</span>
                    </span>
                  </span>
                `
              : nothing}
          </div>
        `,
        rowValues: [
          {
            mainValue: scales[0]?.tierPrices[index]?.price?.formatted,
          },
        ],
      };

      if (grossColumn) {
        newRow.rowValues.unshift(grossColumn);
      }

      reducedData.push(newRow);
    });

    this.reducedPriceScaleData = reducedData;
  }

  render() {
    if (!this.reducedPriceScaleData) {
      return nothing;
    }

    return html`
      <fg-table
        small
        .columnHeadings=${this.hasGrossColumn
          ? [t('content.pieces'), t('labels.gross'), t('labels.net')]
          : [t('content.pieces'), t('labels.price')]}
        .columnRows=${this.reducedPriceScaleData}
      ></fg-table>
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    css`
      :host {
        display: none;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        :host {
          display: block;
        }
      }
    `,
  ];
}
